import React, { useState, useEffect } from "react";
import { Button, Box, LinearProgress, keyframes } from "@mui/material";
import { Typography } from "@mui/material";

const pulse = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.65;
  }
  100% {
    opacity: 1;
  }
`;

export const finalPulse = keyframes`
  0% {
    opacity: 1;
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.4);
  }
  50% {
    opacity: 0.75;
    box-shadow: 0 0 0 8px rgba(0, 0, 0, 0);
  }
  100% {
    opacity: 1;
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
  }
`;

const fadeIn = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;

export const ProgressButton = ({
  loading,
  children,
  loadingText,
  progress = 0,
  isDone = false,
  finalText,
  ...props
}) => {
  const [isProgressDone, setIsProgressDone] = useState(false);
  const [showFinalState, setShowFinalState] = useState(false);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    if (progress >= 100 && !isProgressDone) {
      setIsProgressDone(true);

      // Start fade out
      setIsFading(true);

      // Short transition to final state
      setTimeout(() => {
        setShowFinalState(true);
      }, 800);
    }
  }, [progress, isProgressDone]);

  if (showFinalState) {
    return (
      <Box
        sx={{
          animation: `${fadeIn} 1000ms ease-out`,
          opacity: 1,
        }}
      >
        <Button
          {...props}
          sx={{
            ...props.sx,
            animation: `${finalPulse} 3s infinite`,
            backgroundColor: "#000 !important",
            color: "#fff !important",
            transform: "scale(1.02)",
            transition: "all 1s cubic-bezier(0.5, 0, 0.2, 1)",
          }}
        >
          {children}
        </Button>
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            textAlign: "center",
            color: "primary.main",
          }}
        >
          {finalText}
        </Typography>
      </Box>
    );
  }

  return (
    <Button
      disabled={loading}
      {...props}
      sx={{
        ...props.sx,
        position: "relative",
        overflow: "hidden",
        opacity: isFading ? 0 : 1,
        transition: "opacity 800ms ease-out",
        "&:disabled": {
          backgroundColor: "#000",
          color: "#fff",
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 2 }}>
        {loading ? loadingText : children}
      </Box>
      {loading && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            top: 0,
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            "& .MuiLinearProgress-bar": {
              backgroundImage: `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 12px,
                rgba(255, 255, 255, 0.3) 12px,
                rgba(255, 255, 255, 0.3) 24px
              )`,
              backgroundSize: "34px 34px",
              backgroundColor: "transparent",
              transition: "transform .4s linear",
              ...(isProgressDone && {
                animation: `${pulse} 500ms ease-in-out`,
              }),
            },
            zIndex: 1,
          }}
        />
      )}
    </Button>
  );
};
